import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Zap, Loader2, BatteryCharging } from "lucide-react";
import { fetchPendingCharging, completeCharging } from "@/lib/api";
import { formatDMY } from "@/lib/date";

const fmtDT = (iso) => (iso ? `${formatDMY(iso.slice(0, 10))} ${iso.slice(11, 16)}` : "—");

// Mandatory popup: whenever a charging session has ended without a battery reading,
// the admin must enter battery % before the vehicle becomes available again.
export const ChargingCompletionModal = ({ onCompleted, onDeferToTable }) => {
  const [pending, setPending] = useState([]);
  const [battery, setBattery] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      setPending(await fetchPendingCharging());
    } catch {
      /* ignore (not authed yet / transient) */
    }
  };

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("visibilitychange", onFocus);
    const t = setInterval(refresh, 60000);
    return () => { window.removeEventListener("visibilitychange", onFocus); clearInterval(t); };
  }, []);

  const current = pending[0];
  useEffect(() => { setBattery(""); }, [current?.id]);

  if (!current) return null;

  const submit = async () => {
    const val = Number(battery);
    if (!/^\d+$/.test(String(battery).trim()) || !Number.isInteger(val) || val < 1 || val > 100) {
      toast.error("Enter a whole number from 1 to 100");
      return;
    }
    setSaving(true);
    try {
      await completeCharging(current.id, val);
      toast.success(`${current.vehicle} battery set to ${val}% — vehicle available`);
      await refresh();
      onCompleted?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.info("This session was already completed by another admin");
        await refresh();
        onCompleted?.();
      } else {
        toast.error(err?.response?.data?.detail || "Could not complete charging");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="charging-completion-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#5C2D91]/10 text-[#5C2D91]">
            <BatteryCharging className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-heading text-base font-bold text-[#0F172A]">Battery update required</h3>
            <p className="text-xs font-semibold text-amber-600">Charging Completed – Battery Update Required</p>
          </div>
        </div>
        <p className="text-sm text-[#64748B]">
          Charging just ended for <span className="font-semibold text-[#0F172A]">{current.vehicle}</span>{" "}
          (ended {fmtDT(current.end_at)}). Enter the current battery % to mark it Available.
          {pending.length > 1 && <span className="ml-1 font-medium text-[#5C2D91]">({pending.length} sessions pending)</span>}
        </p>

        <label className="mt-4 block text-xs font-semibold text-[#475569]">Current battery % (1–100)</label>
        <input
          type="number"
          min={1}
          max={100}
          step={1}
          inputMode="numeric"
          value={battery}
          onChange={(e) => setBattery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          autoFocus
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#5C2D91] focus:ring-1 focus:ring-[#5C2D91]"
          data-testid="charging-battery-input"
        />

        <button
          onClick={submit}
          disabled={saving}
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-[#5C2D91] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#4A2475] disabled:opacity-60"
          data-testid="charging-submit"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
          Save battery & mark Available
        </button>
        {onDeferToTable && (
          <button
            onClick={onDeferToTable}
            className="mt-2 w-full text-center text-xs font-semibold text-[#5C2D91] underline-offset-2 hover:underline"
            data-testid="charging-defer-to-table"
          >
            Enter battery in the Maintenance / Charging table instead →
          </button>
        )}
        <p className="mt-2 text-center text-[11px] text-[#94A3B8]">This step is mandatory — the vehicle stays unavailable until battery % is recorded.</p>
      </div>
    </div>
  );
};
